import { useEffect, useState } from 'react';

import updateTenantProfile from '@spotahome/marketplace-common/src/graphql/mutations/tenant-profile';
import getTenantProfile from '@spotahome/marketplace-common/src/graphql/queries/tenant-profile';

import ReCaptcha from 'react-google-recaptcha';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { TOAST_TIMEOUT } from '@spotahome/marketplace-common/src/constants';
import {
  useUserInfoContext,
  Button,
  FormProvider,
  Modal,
  ModalBody,
  ModalHeader,
  showToast,
  GraphQLClients,
  Spinner
} from '@spotahome/ui-library';
import {
  clientErrorTracker,
  trans,
  useSoyuzClientConfig,
  useSoyuzLocales
} from '@spotahome/soyuz/client';
import { createZendeskTicketLandlord } from '@spotahome/ui-library/src/tenant/mutations/zendesk';

import './ListingDoubtsLandlordModal.scss';

const formSchema = yup.object().shape({
  comment: yup.string().ensure().required('common.form.required-field'),
  phoneNumber: yup.string().ensure().required('common.form.required-field'),
  moveInDate: yup.string().ensure().required('common.form.required-field')
});

const ListingDoubtsLandlordModal = ({
  handleClose,
  isOpen,
  listingId,
  listingAddress,
  listingUrl,
  landlordId,
  cityId,
  countryCode
}) => {
  const { current: currentLocale } = useSoyuzLocales();
  const { recaptchaSiteKeys } = useSoyuzClientConfig();
  const [recaptchaPassed, setRecaptchaPassed] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isErrorActive, setErrorActive] = useState(false);
  const [profilePhone, setProfilePhone] = useState('');
  const [isTenantProfileLoading, setTenantProfileLoading] = useState(true);

  const { userInfo } = useUserInfoContext();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setTenantProfileLoading(true);
        const data = await getTenantProfile(GraphQLClients.transactional());
        setProfilePhone(data.tenantProfile?.phone ?? '');
        setTenantProfileLoading(false);
      } catch (error) {
        clientErrorTracker.error({ error, source: 'ListingDoubts-GetProfile' });
        setTenantProfileLoading(false);
      }
    };

    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen]);

  const displayThankYouToast = () => {
    showToast(
      {
        renderTitle: () => trans('listing.doubts.landlord.toast.title'),
        renderContent: () => trans('listing.doubts.landlord.toast.text')
      },
      { autoClose: TOAST_TIMEOUT }
    );
  };

  const saveTenantProfile = params =>
    updateTenantProfile(GraphQLClients.transactional())(params);

  const handleFormSubmit = async values => {
    SoyuzAnalytics.sendGA4Event('send', {
      action: 'listing-doubts-landlord-attempt'
    });

    setLoading(true);
    setErrorActive(false);

    if (values.phoneNumber !== profilePhone) {
      try {
        await saveTenantProfile({
          phone: values.phoneNumber
        });
        setProfilePhone(values.phoneNumber);
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: 'ListingDoubts-UpdatePhone'
        });
      }
    }

    const input = {
      motive: 'tt_ll_chat',
      doubtMotive: 'tt_ll_chat',
      doubtSubmotive: 'tt_ll_chat',
      responseUser: 'Artificial Intelligence',
      city: cityId,
      country: countryCode,
      tenantId: userInfo?.id,
      landlordId,
      tenantEmail: userInfo?.email,
      ruId: listingId,
      propertyAddress: listingAddress,
      userType: 'Tenant',
      phone: values.phoneNumber,
      moveInDate: values.moveInDate,
      listingUrl,
      tenantMessage: `${values.comment.replaceAll(
        '\n',
        '\\n'
      )}\\nProperty Id:${listingId}`,
      channel: 'Chat'
    };

    try {
      await createZendeskTicketLandlord(GraphQLClients.marketplace())({
        input
      });
      SoyuzAnalytics.sendGA4Event('send', {
        action: 'listing-doubts-landlord-success'
      });
      setLoading(false);
      handleClose();
      displayThankYouToast();
    } catch (error) {
      setLoading(false);
      clientErrorTracker.error({ error, source: 'ListingDoubts' });
      SoyuzAnalytics.sendGA4Event('send', {
        action: 'listing-doubts-landlord-error'
      });
      setErrorActive(true);
    }
  };

  const handleFormError = () => {
    setLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      size="medium"
      contentLabel="modal"
      ariaHideApp={false}
      onRequestClose={handleClose}
      className="listing-doubts-modal"
    >
      <ModalHeader
        label={trans('listing.doubts.landlord.modal.title')}
        onRequestClose={handleClose}
        className="listing-doubts-modal__header"
      />
      <ModalBody className="listing-doubts-modal__body">
        <div className="listing-doubts-modal__text">
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: trans('listing.doubts.landlord.modal.text')
            }}
          />
          <Button
            color="link"
            onClick={handleClose}
            className="listing-doubts-modal__contact-us-button"
          >
            {trans('listing.doubts.landlord.modal.close-text')}
          </Button>
        </div>

        {isTenantProfileLoading ? (
          <Spinner />
        ) : (
          <FormProvider
            fieldsClassName="listing-doubts-modal__fields"
            disabled={!recaptchaPassed}
            schema={formSchema}
            onSubmit={handleFormSubmit}
            onError={handleFormError}
            isLoading={isLoading}
            initialValues={{
              phoneNumber: profilePhone
            }}
          >
            <div className="listing-doubts-modal__form">
              <FormProvider.Input
                name="comment"
                title={trans('contact-landlord.field.comment.title')}
                placeholder={trans(
                  'contact-landlord.field.comment.placeholder'
                )}
                type="textarea"
                rows={3}
              />
              <FormProvider.PhoneInput
                id="phoneNumber"
                name="phoneNumber"
                title={trans('contact-landlord.field.phoneNumber.title')}
                required
              />

              <FormProvider.Date
                name="moveInDate"
                title={trans('contact-landlord.field.moveInDate.title')}
                placeholder={trans(
                  'contact-landlord.field.moveInDate.placeholder'
                )}
              />

              <ReCaptcha
                hl={currentLocale}
                sitekey={recaptchaSiteKeys.spotahome}
                onChange={() => setRecaptchaPassed(true)}
                onExpired={() => setRecaptchaPassed(false)}
              />

              <div className="listing-doubts-modal__buttons">
                <Button
                  color="tertiary"
                  onClick={handleClose}
                  size="big-redesign"
                  className="listing-doubts-modal__close-button"
                >
                  {trans('listing.doubts.landlord.modal.button.close-text')}
                </Button>

                <FormProvider.Submit>
                  {isLoading
                    ? trans('loading')
                    : trans('listing.doubts.landlord.form.submit')}
                </FormProvider.Submit>
              </div>

              {isErrorActive && (
                <p className="listing-doubts-modal__error">
                  {trans('contact-us.form-error')}
                </p>
              )}
            </div>
          </FormProvider>
        )}
      </ModalBody>
    </Modal>
  );
};

ListingDoubtsLandlordModal.propTypes = {
  listingId: PropTypes.string.isRequired,
  listingAddress: PropTypes.string.isRequired,
  listingUrl: PropTypes.string.isRequired,
  landlordId: PropTypes.string.isRequired,
  cityId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  countryCode: PropTypes.string.isRequired
};

export default ListingDoubtsLandlordModal;
